/**
 * FontLibraryContext — loads the brand's font manifest once and registers
 * every variant with the document via the FontFace API, so all editor
 * surfaces (OverlayPreview, TendV2ContentCard text layers, the Remotion
 * <Player> preview, and font-picker option previews) render real glyphs.
 *
 * Fonts register page-globally (document.fonts); a module-level set dedupes
 * across provider remounts. Consumers without a provider get a null-object
 * and degrade to the Inter fallback stack — zero regression.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fontsApi } from '../api/fontsApi';
import type {
  FontManifest,
  FontManifestFamily,
} from '../utils/fontResolution';

interface FontLibraryValue {
  manifest: FontManifest | null;
  isLoaded: boolean;
  getFamilyBySlug: (slug: string) => FontManifestFamily | undefined;
  refresh: () => void;
}

const NULL_VALUE: FontLibraryValue = {
  manifest: null,
  isLoaded: false,
  getFamilyBySlug: () => undefined,
  refresh: () => undefined,
};

const FontLibraryContext = createContext<FontLibraryValue>(NULL_VALUE);

// family|weight|style|url → registered (document.fonts is page-global)
const registered = new Set<string>();

function registerManifestFonts(manifest: FontManifest): void {
  if (typeof FontFace === 'undefined') return;
  manifest.families.forEach((family) => {
    family.variants.forEach((variant) => {
      const key = `${family.cssFamily}|${variant.weight}|${variant.style}|${variant.url}`;
      if (registered.has(key)) return;
      registered.add(key);
      try {
        const face = new FontFace(family.cssFamily, `url(${variant.url})`, {
          weight: String(variant.weight),
          style: variant.style,
        });
        (document as any).fonts.add(face);
        face.load().catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn(`Font load failed: ${family.cssFamily} ${variant.weight} ${variant.style}`, err);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`FontFace registration failed: ${family.cssFamily}`, err);
      }
    });
  });
}

interface FontLibraryProviderProps {
  brandSlug: string | null | undefined;
  children: React.ReactNode;
}

export const FontLibraryProvider: React.FC<FontLibraryProviderProps> = ({
  brandSlug,
  children,
}) => {
  const [manifest, setManifest] = useState<FontManifest | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!brandSlug) {
      setManifest(null);
      setIsLoaded(false);
      return undefined;
    }
    fontsApi
      .getManifest(brandSlug)
      .then((m) => {
        if (cancelled) return;
        setManifest(m);
        registerManifestFonts(m);
        setIsLoaded(true);
      })
      .catch((err) => {
        // Editor keeps working with Inter fallback.
        // eslint-disable-next-line no-console
        console.warn('Font manifest load failed; using Inter fallback', err);
        if (!cancelled) {
          setManifest(null);
          setIsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [brandSlug, reloadKey]);

  const value = useMemo<FontLibraryValue>(
    () => ({
      manifest,
      isLoaded,
      getFamilyBySlug: (slug: string) => manifest?.families.find((f) => f.slug === slug),
      refresh: () => setReloadKey((k) => k + 1),
    }),
    [manifest, isLoaded],
  );

  return <FontLibraryContext.Provider value={value}>{children}</FontLibraryContext.Provider>;
};

/** Null-object safe: without a provider, everything degrades to Inter. */
export const useFontLibrary = (): FontLibraryValue => useContext(FontLibraryContext);

export default FontLibraryContext;
